/**
 * Fuzzy matching and clustering for vendor invoice line items
 * 
 * Enhanced with advanced similarity algorithms:
 * - N-gram character matching
 * - Phonetic similarity (Soundex)
 * - Domain-specific normalization
 */

import { 
  calculateAdvancedSimilarity, 
  nGramSimilarity, 
  phoneticSimilarity,
  tokenSimilarity 
} from './advancedSimilarity';

export interface InvoiceLineItem {
  id?: string;
  name: string;
  quantity?: number;
  unit?: string;
  price?: number;
  total?: number;
  description?: string;
}

export interface LineItemCluster {
  id: string;
  cluster_name: string;
  raw_line_items: InvoiceLineItem[];
  confidence_score: number;
}

/**
 * Calculates Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  const len1 = str1.length;
  const len2 = str2.length;
  
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1
        );
      }
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Calculates similarity score between two strings (0-1)
 * Enhanced with N-gram and phonetic matching for better product name matching
 */
function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1.0;
  if (!s1 || !s2) return 0;
  
  // Use multiple similarity measures for product names
  const maxLen = Math.max(s1.length, s2.length);
  const distance = levenshteinDistance(s1, s2);
  const levenshteinSim = 1 - (distance / maxLen);
  
  // N-gram similarity (good for abbreviations and typos)
  const nGramSim = nGramSimilarity(s1, s2, 3);
  
  // Phonetic similarity (catches sound-alike products)
  const phoneticSim = phoneticSimilarity(s1, s2);
  
  // Token/Jaccard similarity
  const tokenSim = tokenSimilarity(s1, s2, 2);
  
  // Weighted combination optimized for product names
  // Levenshtein: 30%, N-gram: 30%, Phonetic: 20%, Token: 20%
  return levenshteinSim * 0.30 + nGramSim * 0.30 + phoneticSim * 0.20 + tokenSim * 0.20;
}

/**
 * Normalizes a product name for comparison
 * Improved to preserve more meaningful information and handle product codes
 * Enhanced to handle size variants (RS, SM, LG, MD, XL, etc.) for better grouping
 */
function normalizeProductName(name: string): string {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ') // Remove special characters but keep hyphens (e.g., "FF3-8")
    // Remove size variant suffixes (RS, SM, LG, MD, XL, etc.) - these should not split groups
    .replace(/\b(rs|sm|md|lg|xl|xxl|xxxl|s|m|l|regular|small|medium|large|extra\s*large)\b/gi, '')
    .replace(/\b(cs|bx|pk|case|box|pack|ct|count|ea|each|per)\b/gi, '') // Remove package indicators
    .replace(/\b\d+\s*(oz|lb|lbs|g|kg|ml|l|gal|pt|qt)\b/gi, '') // Remove size indicators like "12oz", "5lb"
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

/**
 * Extracts the core product identifier (handles product codes like "FF3-8")
 */
function extractCoreProductId(name: string): string {
  if (!name) return '';
  
  // Check for product codes (alphanumeric with hyphens, like "FF3-8", "SKU-123")
  const productCodeMatch = name.match(/\b([A-Za-z]{1,4}\d{1,3}[-]?\d{0,3})\b/);
  if (productCodeMatch) {
    return productCodeMatch[1].toUpperCase();
  }
  
  // Otherwise, normalize and return
  return normalizeProductName(name);
}

/**
 * Checks if one string contains another as a significant substring
 */
function hasSignificantSubstring(str1: string, str2: string, minLength: number = 4): boolean {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  // Check if one contains the other
  if (s1.includes(s2) && s2.length >= minLength) return true;
  if (s2.includes(s1) && s1.length >= minLength) return true;
  
  // Check for common significant substring (word-based)
  const words1 = s1.split(/\s+/).filter(w => w.length >= minLength);
  const words2 = s2.split(/\s+/).filter(w => w.length >= minLength);
  
  for (const w1 of words1) {
    for (const w2 of words2) {
      if (w1 === w2 || w1.includes(w2) || w2.includes(w1)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Extracts key words from a product name (removes common words)
 */
function extractKeyWords(name: string): string[] {
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'lb', 'lbs', 'oz', 'ozs', 'pound', 'pounds', 'ounce', 'ounces', 'each', 'ea', 'pk', 'pack',
    'case', 'cs', 'box', 'bx', 'bag', 'bg', 'can', 'cn', 'bottle', 'btl', 'jar', 'jr',
  ]);
  
  const normalized = normalizeProductName(name);
  return normalized
    .split(' ')
    .filter(word => word.length > 2 && !stopWords.has(word));
}

/**
 * Calculates similarity between two line items
 * Enhanced with product code matching and substring matching
 */
export function calculateLineItemSimilarity(item1: InvoiceLineItem, item2: InvoiceLineItem): number {
  const name1 = normalizeProductName(item1.name);
  const name2 = normalizeProductName(item2.name);
  
  // Check for identical product codes first (high priority match)
  const productId1 = extractCoreProductId(item1.name);
  const productId2 = extractCoreProductId(item2.name);
  if (productId1 && productId2 && productId1 === productId2) {
    return 0.95; // Very high similarity for matching product codes
  }
  
  // Check for significant substring match
  if (hasSignificantSubstring(name1, name2, 4)) {
    // Substring match provides a baseline boost
    const baseSimilarity = 0.75;
    const nameSimilarity = calculateSimilarity(name1, name2);
    return Math.max(baseSimilarity, nameSimilarity);
  }
  
  // Direct name similarity
  const nameSimilarity = calculateSimilarity(name1, name2);
  
  // Keyword overlap
  const keywords1 = new Set(extractKeyWords(item1.name));
  const keywords2 = new Set(extractKeyWords(item2.name));
  const intersection = new Set([...keywords1].filter(x => keywords2.has(x)));
  const union = new Set([...keywords1, ...keywords2]);
  const keywordSimilarity = union.size > 0 ? intersection.size / union.size : 0;
  
  // Combined similarity (weighted average)
  let combinedSimilarity = (nameSimilarity * 0.6) + (keywordSimilarity * 0.4);
  
  // Boost similarity if units match
  if (item1.unit && item2.unit && item1.unit.toLowerCase() === item2.unit.toLowerCase()) {
    combinedSimilarity = Math.min(combinedSimilarity * 1.1, 1.0);
  }
  
  return combinedSimilarity;
}

/**
 * Groups similar line items into clusters
 * Improved to reduce singletons and merge similar groups
 */
export function groupLineItemsIntoClusters(
  lineItems: InvoiceLineItem[],
  similarityThreshold: number = 0.75 // Default threshold - can be adjusted for stricter (0.85) or looser (0.70) matching
): LineItemCluster[] {
  if (lineItems.length === 0) {
    return [];
  }
  
  const clusters: LineItemCluster[] = [];
  const processed = new Set<number>();
  
  // First pass: initial clustering
  for (let i = 0; i < lineItems.length; i++) {
    if (processed.has(i)) continue;
    
    const currentItem = lineItems[i];
    const cluster: InvoiceLineItem[] = [currentItem];
    processed.add(i);
    
    // Find similar items
    for (let j = i + 1; j < lineItems.length; j++) {
      if (processed.has(j)) continue;
      
      const similarity = calculateLineItemSimilarity(currentItem, lineItems[j]);
      if (similarity >= similarityThreshold) {
        cluster.push(lineItems[j]);
        processed.add(j);
      }
    }
    
    // Generate cluster name (use the most common name or longest name)
    const clusterName = generateClusterName(cluster);
    
    // Calculate confidence score based on cluster size and similarity
    const avgSimilarity = calculateAverageSimilarity(cluster);
    const confidenceScore = Math.min(0.5 + (cluster.length * 0.1) + (avgSimilarity * 0.3), 0.95);
    
    clusters.push({
      id: `cluster_${i}_${Date.now()}`,
      cluster_name: clusterName,
      raw_line_items: cluster,
      confidence_score: confidenceScore,
    });
  }
  
  // Second pass: refine clusters by merging similar ones
  // This helps reduce duplicates and fragmented groups
  const refinedClusters = refineClusters(clusters);
  
  // Sort clusters by number of line items (highest first) - ID 40
  return refinedClusters.sort((a, b) => b.raw_line_items.length - a.raw_line_items.length);
}

/**
 * Generates a cluster name from grouped items
 */
function generateClusterName(items: InvoiceLineItem[]): string {
  if (items.length === 0) return 'Unknown Item';
  if (items.length === 1) return items[0].name;
  
  // Find the longest name (likely most descriptive)
  const longestName = items.reduce((longest, item) => 
    item.name.length > longest.length ? item.name : longest, 
    items[0].name
  );
  
  // Or find the most common words across all names
  const allWords = items.flatMap(item => extractKeyWords(item.name));
  const wordCounts = new Map<string, number>();
  allWords.forEach(word => {
    wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
  });
  
  // Get words that appear in at least 50% of items
  const commonWords = Array.from(wordCounts.entries())
    .filter(([_, count]) => count >= Math.ceil(items.length * 0.5))
    .sort((a, b) => b[1] - a[1])
    .map(([word]) => word)
    .slice(0, 3);
  
  if (commonWords.length > 0) {
    return commonWords.join(' ').toUpperCase();
  }
  
  return longestName;
}

/**
 * Calculates average similarity within a cluster
 */
function calculateAverageSimilarity(items: InvoiceLineItem[]): number {
  if (items.length <= 1) return 1.0;
  
  let totalSimilarity = 0;
  let comparisons = 0;
  
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      totalSimilarity += calculateLineItemSimilarity(items[i], items[j]);
      comparisons++;
    }
  }
  
  return comparisons > 0 ? totalSimilarity / comparisons : 0;
}

/**
 * Merges two clusters if they're similar enough
 */
export function mergeClustersIfSimilar(
  cluster1: LineItemCluster,
  cluster2: LineItemCluster,
  similarityThreshold: number = 0.80 // Increased from 0.7 to reduce false merges
): LineItemCluster | null {
  // Compare representative items from each cluster
  const item1 = cluster1.raw_line_items[0];
  const item2 = cluster2.raw_line_items[0];
  
  const similarity = calculateLineItemSimilarity(item1, item2);
  
  if (similarity >= similarityThreshold) {
    return {
      id: cluster1.id,
      cluster_name: generateClusterName([...cluster1.raw_line_items, ...cluster2.raw_line_items]),
      raw_line_items: [...cluster1.raw_line_items, ...cluster2.raw_line_items],
      confidence_score: Math.min(
        (cluster1.confidence_score + cluster2.confidence_score) / 2,
        0.95
      ),
    };
  }
  
  return null;
}

/**
 * Checks if two clusters should be merged based on identical core product IDs
 */
function haveSameCoreProduct(cluster1: LineItemCluster, cluster2: LineItemCluster): boolean {
  // Extract product IDs from all items in both clusters
  const getProductIds = (cluster: LineItemCluster): Set<string> => {
    const ids = new Set<string>();
    for (const item of cluster.raw_line_items) {
      const productId = extractCoreProductId(item.name);
      if (productId && productId.length >= 3) {
        ids.add(productId);
      }
    }
    return ids;
  };
  
  const ids1 = getProductIds(cluster1);
  const ids2 = getProductIds(cluster2);
  
  // If either has no product IDs, can't match by product ID
  if (ids1.size === 0 || ids2.size === 0) return false;
  
  // Check for any common product IDs
  for (const id of ids1) {
    if (ids2.has(id)) return true;
  }
  
  return false;
}

/**
 * Checks if two clusters have identical normalized core names
 */
function haveIdenticalCoreNames(cluster1: LineItemCluster, cluster2: LineItemCluster): boolean {
  const getName = (cluster: LineItemCluster): string => {
    // Get the most representative name (from first item, normalized)
    const item = cluster.raw_line_items[0];
    if (!item) return '';
    return normalizeProductName(item.name);
  };
  
  const name1 = getName(cluster1);
  const name2 = getName(cluster2);
  
  if (!name1 || !name2) return false;
  
  // Check for exact match
  if (name1 === name2) return true;
  
  // Check for significant substring match (one contains the other)
  if (name1.length >= 4 && name2.length >= 4) {
    if (name1.includes(name2) || name2.includes(name1)) return true;
  }
  
  return false;
}

/**
 * Refines clusters by merging similar ones
 * Improved with multiple passes to catch transitive similarities
 */
export function refineClusters(clusters: LineItemCluster[]): LineItemCluster[] {
  let refined: LineItemCluster[] = [...clusters];
  let hasChanges = true;
  let passCount = 0;
  const maxPasses = 3; // Allow multiple passes for better merging
  
  // Multiple merge passes to catch clusters that become similar after initial merges
  while (hasChanges && passCount < maxPasses) {
    hasChanges = false;
    passCount++;
    const merged = new Set<number>();
    const newRefined: LineItemCluster[] = [];
    
    for (let i = 0; i < refined.length; i++) {
      if (merged.has(i)) continue;
      
      let currentCluster = refined[i];
      
      // Try to merge with other clusters
      for (let j = i + 1; j < refined.length; j++) {
        if (merged.has(j)) continue;
        
        // First check for identical core product IDs (high priority)
        if (haveSameCoreProduct(currentCluster, refined[j])) {
          currentCluster = {
            id: currentCluster.id,
            cluster_name: generateClusterName([...currentCluster.raw_line_items, ...refined[j].raw_line_items]),
            raw_line_items: [...currentCluster.raw_line_items, ...refined[j].raw_line_items],
            confidence_score: Math.min((currentCluster.confidence_score + refined[j].confidence_score) / 2, 0.95),
          };
          merged.add(j);
          hasChanges = true;
          continue;
        }
        
        // Check for identical core names
        if (haveIdenticalCoreNames(currentCluster, refined[j])) {
          currentCluster = {
            id: currentCluster.id,
            cluster_name: generateClusterName([...currentCluster.raw_line_items, ...refined[j].raw_line_items]),
            raw_line_items: [...currentCluster.raw_line_items, ...refined[j].raw_line_items],
            confidence_score: Math.min((currentCluster.confidence_score + refined[j].confidence_score) / 2, 0.95),
          };
          merged.add(j);
          hasChanges = true;
          continue;
        }
        
        // Standard similarity-based merge
        const mergedCluster = mergeClustersIfSimilar(currentCluster, refined[j], 0.70); // Lowered from 0.75 for better merging
        if (mergedCluster) {
          currentCluster = mergedCluster;
          merged.add(j);
          hasChanges = true;
        }
      }
      
      newRefined.push(currentCluster);
      merged.add(i);
    }
    
    refined = newRefined;
  }
  
  return refined;
}

